'use client';

import { useCallback, useState } from 'react';
import {
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { FM_CONTEXT_MENU_SURFACE } from '@/components/ui/menu-surface';
import { Save, Folder, FolderPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/providers/i18n';
import { ASSET_FOLDERS_EVENTS, dispatchAssetFoldersEvent } from '@/components/canvas/utils/asset-folders-events';
import {
  type AssetFolder as FolderNode,
  fetchAssetFolders,
  groupFoldersByParent,
} from '@/components/canvas/utils/asset-folders-api';

type Scope = 'personal' | 'team';

/**
 * Resolve the storage object key from a public asset URL so we can match it
 * against `file_asset.storagePath`. Supports Supabase (object/render) and OSS.
 */
function storagePathFromUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    const markers = [
      '/storage/v1/object/public/files/',
      '/storage/v1/render/image/public/files/',
    ];
    for (const m of markers) {
      const i = u.pathname.indexOf(m);
      if (i >= 0) {
        return decodeURIComponent(u.pathname.slice(i + m.length)).replace(/^\/+/, '');
      }
    }
    if (u.hostname.includes('aliyuncs.com')) {
      return decodeURIComponent(u.pathname).replace(/^\/+/, '');
    }
    return null;
  } catch {
    return null;
  }
}

function stripQuery(raw: string): string {
  const i = raw.indexOf('?');
  return i >= 0 ? raw.slice(0, i) : raw;
}

/**
 * Context-menu submenu that saves a node's media into the asset library by
 * moving the matching `file_asset` into the chosen folder. Scope follows the
 * project (team project -> team library, otherwise personal).
 */
export function SaveToLibrarySubmenu({
  url,
  mediaType,
  name,
  scope,
  projectId,
  disabled,
}: {
  url: string;
  mediaType: 'image' | 'video' | 'audio';
  name: string;
  scope: Scope;
  projectId?: string | null;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const [folders, setFolders] = useState<FolderNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      setFolders(await fetchAssetFolders(scope));
    } catch {
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const save = useCallback(
    async (folderId: string | null) => {
      const storagePath = storagePathFromUrl(url);
      if (!storagePath) {
        toast.error(t('Could not save this asset'));
        return;
      }
      setSaving(true);
      try {
        // Make sure the asset is registered in the library (no-op if it already
        // exists — the storage_path unique key dedupes), then move it.
        const fallbackName = storagePath.split('/').pop() || name || mediaType;
        await fetch('/api/library', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            storagePath,
            publicUrl: stripQuery(url),
            type: mediaType,
            name: name?.trim() || fallbackName,
            projectId: projectId ?? undefined,
          }),
        }).catch(() => {});

        const res = await fetch('/api/library', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storagePaths: [storagePath], folderId, scope }),
        });
        if (!res.ok) throw new Error('move failed');

        toast.success(t('Saved to library'));
        // Explicit user action — refresh immediately (no debounce) so the saved
        // asset shows right away if the panel is open.
        dispatchAssetFoldersEvent(ASSET_FOLDERS_EVENTS.refresh);
      } catch {
        toast.error(t('Failed to save'));
      } finally {
        setSaving(false);
      }
    },
    [url, mediaType, name, projectId, scope, t],
  );

  // Create a new top-level folder, then save the asset into it.
  const createAndSave = useCallback(
    async (rawName: string) => {
      const folderName = rawName.trim();
      if (!folderName) {
        setCreating(false);
        return;
      }
      setSaving(true);
      try {
        const res = await fetch('/api/asset-folders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: folderName, scope }),
        });
        const data = await res.json().catch(() => ({}));
        const created = data?.item ?? data;
        const newId = created?.id ? String(created.id) : null;
        if (!res.ok || !newId) throw new Error('create failed');
        // Reflect the new folder in the menu immediately.
        setFolders((prev) => [...prev, { id: newId, name: folderName, parentId: null }]);
        setCreating(false);
        await save(newId);
      } catch {
        toast.error(t('Failed to save'));
        setSaving(false);
      }
    },
    [scope, save, t],
  );

  const byParent = groupFoldersByParent(folders);

  // Render one folder level at a time; folders with children expand into a
  // nested submenu (with a "save here" item) instead of flattening the tree.
  const renderFolder = (node: FolderNode) => {
    const children = byParent.get(node.id) ?? [];
    if (children.length === 0) {
      return (
        <ContextMenuItem key={node.id} onClick={() => save(node.id)} disabled={saving}>
          <Folder size={12} className="shrink-0 text-muted-foreground" />
          <span className="truncate">{node.name}</span>
        </ContextMenuItem>
      );
    }
    return (
      <ContextMenuSub key={node.id}>
        <ContextMenuSubTrigger className="gap-2" disabled={saving}>
          <Folder size={12} className="shrink-0 text-muted-foreground" />
          <span className="truncate">{node.name}</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className={`${FM_CONTEXT_MENU_SURFACE} max-h-[320px] w-48 overflow-y-auto`}>
          <ContextMenuItem onClick={() => save(node.id)} disabled={saving}>
            <Save size={12} className="text-muted-foreground" />
            <span>{t('Save here')}</span>
          </ContextMenuItem>
          <ContextMenuSeparator />
          {children.map(renderFolder)}
        </ContextMenuSubContent>
      </ContextMenuSub>
    );
  };

  const rootFolders = byParent.get(null) ?? [];

  return (
    <ContextMenuSub
      onOpenChange={(open) => {
        if (open) loadFolders();
        if (!open) setCreating(false);
      }}
    >
      <ContextMenuSubTrigger className="gap-2" disabled={disabled || saving}>
        {saving ? (
          <Loader2 size={12} className="animate-spin text-muted-foreground" />
        ) : (
          <Save size={12} className="text-muted-foreground" />
        )}
        <span>{t('Save asset')}</span>
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className={`${FM_CONTEXT_MENU_SURFACE} max-h-[320px] w-48 overflow-y-auto`}>
        {loading && folders.length === 0 ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 size={14} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {creating ? (
              <div className="px-1.5 py-1" onKeyDown={(e) => e.stopPropagation()}>
                <input
                  autoFocus
                  disabled={saving}
                  placeholder={t('Folder name')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      createAndSave(e.currentTarget.value);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setCreating(false);
                    }
                  }}
                  className="w-full min-w-0 rounded-md border border-sidebar-border/40 bg-sidebar-accent/10 px-2 py-1 text-xs text-foreground/90 outline-none placeholder:text-muted-foreground/50"
                />
              </div>
            ) : (
              <ContextMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setCreating(true);
                }}
                disabled={saving}
              >
                <FolderPlus size={12} className="text-muted-foreground" />
                <span>{t('New folder')}</span>
              </ContextMenuItem>
            )}
            <ContextMenuSeparator />
            {rootFolders.length > 0 ? (
              rootFolders.map(renderFolder)
            ) : (
              <div className="px-2 py-2 text-center text-xs text-muted-foreground/70">
                {t('No folders')}
              </div>
            )}
          </>
        )}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}
